<template>
  <div id="app">
    <sidebar
      v-if="isAuthenticated && isBaseURL && !fullScreenMode"
      :app-name="appName"
      :app-short-name="appShortName"
      :app-version="appVersion"
      :is-collapsed-by-default="false"
      :license="license"
      :menu="menu"
      @sign-out="signOut"
    />
    <div class="main-wrapper">
      <div v-if="!fullScreenMode" class="header-actions-block" :style="`background:${isAuthenticated ? '#f7f7f9' : '#fff; padding-top: 10px;'}`">
        <div class="header-actions-block-left">
          <GoBack v-if="Object.keys($route.params).length !== 0 || $route.meta.isGoBackBtn" />
          <h3 v-if="isAuthenticated" id="header-title" class="header-title">
            {{ $route.meta.title | i18n }}
          </h3>
        </div>
        <div class="header-actions-block-right">
          <Language v-if="license && isBaseURL && !fullScreenMode" />
          <div
            v-if="isAuthenticated && isBaseURL && license && !fullScreenMode && isONEProject"
            v-b-tooltip.hover="{ placement: 'bottomleft', variant: 'secondary' }"
            :title="'open_personal_meeting_room' | i18n"
            class="header-user-icon-btn"
            @click="goToPersonalMeetingRoom"
          >
            <img :src="CameraBlue" />
          </div>
          <div
            v-if="isAuthenticated && isBaseURL && license && !fullScreenMode"
            v-b-tooltip.hover="{ placement: 'bottomleft', variant: 'secondary' }"
            :title="'show_QR_code' | i18n"
            class="header-user-icon-btn"
            @click="showQRCode"
          >
            <img :src="QR" />
          </div>
          <UserMenu v-if="isAuthenticated && isBaseURL && license && !fullScreenMode" :user-data="auth" />
        </div>
      </div>
      <div :class="isAuthenticated && !fullScreenMode ? 'page-content' : 'page-content-full-screen'" :style="fullScreenMode ? `padding:0` : ''">
        <router-view v-if="license && isBaseURL" />
        <Loader v-else />
      </div>
    </div>
    <notifications group="app" width="400" />
    <LicenseInfo v-if="isAuthenticated && license && isSuperadmin" :license="license" />
    <UpdateModal v-if="isAuthenticated && license && isSuperadmin" />
    <ChangePassword v-if="isAuthenticated" />
    <support-modal />
    <QRCodeModal id="QRCodeGlobalModal" :qr-code-id="qrCodeId" :is-personal="true" />
  </div>
</template>

<script>
import api from './v2/api/index';

import QR from './assets/icons/qr.svg';
import CameraBlue from './assets/icons/cameraBlue.svg';
import Sidebar from './plugins/sidebar/sidebar';
import appConfig from './app.config';
import LicenseInfo from './v2/modals/license-info.vue';
import UpdateModal from './v2/modals/update-modal';
import ChangePassword from './v2/modals/change-password';
import QRCodeModal from './v2/modals/QRCode/index.vue';
import SupportModal from './components/support';
import UserMenu from './components/user-menu';
import { ScreenSizeMixin, AuthMixin, LicenseMixin, AuthStorageMixin, ApiMixin, FirebaseMixinOne, NotificationMixin } from './mixins';
import Language from './components/language';
import GoBack from './components/go-back';
import menuConfig from './menu-config/index';
import { LICENSE_MODULES, ACCESS_TO_REPORTS, ROLES_NAMES } from '../../../constants';
import Loader from './v2/components/common/loader';
import { initBaseURL } from './v2/api/service';

export default {
  name: 'App',
  components: { Language, LicenseInfo, Sidebar, SupportModal, Loader, QRCodeModal, ChangePassword, UserMenu, GoBack, UpdateModal },
  mixins: [ScreenSizeMixin, AuthMixin, LicenseMixin, AuthStorageMixin, ApiMixin, FirebaseMixinOne, NotificationMixin],
  data: function () {
    return {
      QR: QR,
      CameraBlue: CameraBlue,
      fullScreenMode: false,
      lockWath: true,
      isBaseURL: false,
      appName: appConfig.appName,
      appShortName: appConfig.appShortName,
      appVersion: appConfig.appVersion,
      menu: [],
      ROLES_NAMES: ROLES_NAMES,
      qrCodeId: null,
    };
  },
  watch: {
    '$route.params.search': {
      handler: function () {
        const fullList = ['project', 'meeting-room'];
        if (!this.fullScreenMode && fullList.includes(this.$route.name)) {
          this.fullScreenMode = true;
        }
        if (this.fullScreenMode && !fullList.includes(this.$route.name)) {
          this.fullScreenMode = false;
        }
      },
      deep: true,
      immediate: true,
    },
    async isAuthenticated() {
      if (this.lockWath) {
        return;
      }
      if (this.auth) {
        if (this.license.modules.includes(LICENSE_MODULES.ONE)) {
          await this.firebaseServiceOne.initFirebase();
        }
        this.initMenu(this.auth);
      }
    },
  },
  async created() {
    await initBaseURL();
    this.isBaseURL = true;
    await this.loadLicense();
    if (this.auth) {
      this.initMenu(this.auth);
    }
    this.lockWath = false;
  },
  methods: {
    goToPersonalMeetingRoom() {
      this.$router.push({ name: 'meeting-room', params: { meeting: this.auth.id } });
    },
    async showQRCode() {
      try {
        await api.qrManagement.post({ id: this.auth.id, expiresIn: 86400 });
      } catch (e) {
        console.log(e);
      }

      try {
        const qrs = await api.qrManagement.getForUser(this.auth.id);
        console.log(qrs, 'qrs');
        const qr = qrs.find((q) => q.expiresIn == 86400);

        if (qr) {
          this.qrCodeId = qr.id;
          setTimeout(() => {
            this.$root.$emit('bv::show::modal', 'QRCodeGlobalModal');
          }, 10);
        } else {
          this.showNotification({ type: 'error', message: this.$translate('Something went wrong') });
        }
      } catch (e) {
        console.log(e);
      }
    },
    initMenu(auth) {
      const m = [];
      if (!this.menu.length) {
        console.log(menuConfig, 'menuList');
        if (this.isIIProject) {
          let modules = [...this.license.modules];
          const isReportPart = ACCESS_TO_REPORTS.some((m) => modules.includes(m));
          if (isReportPart) {
            modules.push(LICENSE_MODULES.IS_REPORTS_PART);
          }
          const menuList = ['commonTop', ...modules, 'commonBottom'];
          menuList.map((item) => {
            if (!item || !menuConfig.coreMenu[item]) {
              return;
            }
            return menuConfig.coreMenu[item].forEach((i) => {
              if (i.projects && i.projects.length) {
                const exist = i.projects.some((p) => modules.includes(p));
                if (!exist) {
                  return;
                }
              }
              i.items = i.items.filter((item) => {
                if (!item.projects) {
                  return true;
                }
                return item.projects.some((p) => modules.includes(p));
              });
              m.push(i);
            });
          });
        } else {
          this.license.modules.map((item) => menuConfig.coreMenu[item].forEach((i) => m.push(i)));
        }
        this.menu = m;
      }
      this.updateMenuByRole(auth.role);
    },
    updateMenuByRole(role) {
      const m = [];

      this.menu.forEach((h) => {
        const data = { ...h, items: h.items.filter((m) => m.roles === '*' || m.roles.includes(role)) };

        if (!h.roles || h.roles === '*' || (h.roles && h.roles.includes(role))) {
          return m.push(data);
        }
      });

      this.menu = m;
    },
  },
};
</script>

<style scoped lang="scss">
#app {
  overflow: auto;
}
.main-wrapper {
  width: 100%;
  max-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
}
@media screen and (max-width: 500px) {
  .header-user-info {
    font-size: 0.8rem;
    display: flex;
    flex-direction: column;
    text-align: right;
    top: 2px;
  }
}
.header-actions-block {
  display: flex;
  justify-content: space-between;
  .header-actions-block-left {
    display: flex;
    align-items: center;
    .header-title {
      font-weight: 500;
      font-size: 24px;
      line-height: 36px;
      color: #0f2041;
      margin: 0 0 0 20px;
      &:first-letter {
        text-transform: uppercase;
      }
    }
    @media (max-width: 1100px) {
      margin-left: 50px;
    }
  }
  .header-actions-block-right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    .header-user-icon-btn {
      cursor: pointer;
      margin-right: 25px;
      display: flex;
      align-items: center;
      > img {
        width: 20px;
      }
    }
  }
}
</style>
